/**
 * Time-of-day theming.
 *
 * The chief app is read at a station desk at any hour: bright at 09:00 for the
 * morning-after review, in a dark watch room at 02:00. Rather than a toggle
 * nobody finds, the theme follows the clock on the reader's own machine.
 *
 * Local time, deliberately, not UTC: the department's own daylight is what
 * matters, and the browser already knows it.
 */

/** Dark from this hour (inclusive), local time. */
export const DARK_FROM_HOUR = 18;
/** Light from this hour (inclusive), local time. */
export const LIGHT_FROM_HOUR = 6;

export type Theme = "light" | "dark";

/**
 * Pure so it can be tested without mocking the clock: 18:00–05:59 is dark,
 * 06:00–17:59 is light.
 */
export function themeForDate(date: Date): Theme {
  const h = date.getHours();
  return h >= DARK_FROM_HOUR || h < LIGHT_FROM_HOUR ? "dark" : "light";
}

/**
 * Milliseconds until the next switch, so a session left open overnight flips
 * at the boundary instead of polling. Always >= 1000 to rule out a hot loop if
 * we land exactly on the hour.
 */
export function msUntilNextSwitch(date: Date): number {
  const next = new Date(date);
  const h = date.getHours();
  const boundary = h >= LIGHT_FROM_HOUR && h < DARK_FROM_HOUR ? DARK_FROM_HOUR : LIGHT_FROM_HOUR;
  next.setHours(boundary, 0, 0, 0);
  if (next.getTime() <= date.getTime()) {
    next.setDate(next.getDate() + 1);
  }
  return Math.max(1000, next.getTime() - date.getTime());
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

/**
 * Applies the current theme and schedules the next flip. Returns a cleanup
 * function. Re-checks on tab focus too: a laptop that was asleep past sunset
 * would otherwise wake showing the wrong theme with its timer long overdue.
 */
export function startThemeSchedule(now: () => Date = () => new Date()): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const tick = () => {
    const at = now();
    applyTheme(themeForDate(at));
    timer = setTimeout(tick, msUntilNextSwitch(at));
  };

  const onVisible = () => {
    if (document.visibilityState === "visible") {
      if (timer !== undefined) clearTimeout(timer);
      tick();
    }
  };

  tick();
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    if (timer !== undefined) clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
